/**
 * Frontend logger
 *
 * A thin wrapper over console.* that gives us a single chokepoint for client-side logging.
 *
 * - debug/info are no-ops outside development (this is the prod "console stripping" — keeps
 *   the bundle's chatty logs out of end-users' DevTools without a build-tool change).
 * - warn/error always emit (we want these in prod DevTools / a future remote sink).
 * - Metadata passes through a STRICT ALLOW-LIST: only known-safe keys survive; everything
 *   else is replaced with '[REDACTED]'. This is a financial / PII app — never log a response
 *   body or a user/contract object. Pass only the whitelisted fields below.
 */

const isDev = process.env.NODE_ENV === 'development';

// Strict allow-list of metadata keys safe to log. Add new keys deliberately.
const SAFE_META_KEYS = new Set([
  'requestId',
  'status',
  'url',
  'method',
  'errorType',
  'message',
  'code',
  'event',
]);

const safeMeta = (meta) => {
  if (!meta || typeof meta !== 'object') return undefined;
  const out = {};
  for (const [k, v] of Object.entries(meta)) {
    out[k] = SAFE_META_KEYS.has(k) ? v : '[REDACTED]';
  }
  return out;
};

const emit = (method, message, meta) => {
  const m = safeMeta(meta);
  // eslint-disable-next-line no-console
  if (m) console[method](message, m);
  // eslint-disable-next-line no-console
  else console[method](message);
};

const logger = {
  debug: (message, meta) => { if (isDev) emit('debug', message, meta); },
  info: (message, meta) => { if (isDev) emit('info', message, meta); },
  warn: (message, meta) => emit('warn', message, meta),
  error: (message, meta) => emit('error', message, meta),
};

// TODO: remote sink — POST warn/error here to Azure App Insights (web SDK) or a backend
// /logs endpoint when centralized client observability is introduced. safeMeta() already
// guarantees redaction before anything leaves the client.

export default logger;
